import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, FileText } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/motion";
import { EMAIL } from "@/constants/site";

const CHANNELS = [
  {
    n: "01",
    icon: FileText,
    tag: "Primary",
    title: "Shielding Design Request",
    body: "The fastest path to a quote. Complete the structured project intake — most scopes receive a quote within one business day.",
    cta: "Start the request",
    href: "/request",
    external: false,
    testid: "contact-card-request",
    featured: true,
  },
  {
    n: "02",
    icon: Mail,
    tag: "Secondary",
    title: "Email Lancer directly",
    body: "Send your project information — location, floor plan, equipment, workload, and construction — and Heartland Physics will respond with the next step.",
    cta: EMAIL,
    href: `mailto:${EMAIL}`,
    external: false,
    testid: "contact-card-email",
    featured: false,
  },
];

const Contact = () => (
  <>
    <section data-testid="contact-hero" className="border-b border-border bg-blueprint-grid pt-16">
      <div className="mx-auto max-w-7xl px-5 pb-14 pt-14 md:px-8 md:pb-20 md:pt-20">
        <Reveal y={12}>
          <p className="eyebrow">Contact</p>
        </Reveal>
        <h1 className="mt-6 max-w-3xl font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
          <MaskedLines delay={0.1} lines={["Two ways in.", "One physicist", "on the other end."]} />
        </h1>
        <Reveal delay={0.5}>
          <p className="mt-7 max-w-2xl text-base leading-relaxed text-ink/70 md:text-lg" data-testid="contact-intro">
            Every channel reaches Lancer directly. Choose the one that fits where your project is today.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="border-b border-border bg-white">
      <div className="mx-auto max-w-7xl px-5 py-14 md:px-8 md:py-20">
        <div className="grid gap-px border border-border bg-border lg:grid-cols-2">
          {CHANNELS.map((c, i) => {
            const inner = (
              <>
                <div className="flex items-center justify-between">
                  <span className={`font-mono text-xs ${c.featured ? "text-azure" : "text-blueprint"}`}>{c.n}</span>
                  <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${c.featured ? "text-white/60" : "text-ink/40"}`}>{c.tag}</span>
                </div>
                <c.icon size={22} className={`mt-8 ${c.featured ? "text-azure" : "text-blueprint"}`} />
                <h2 className={`mt-5 font-heading text-base font-bold tracking-tight ${c.featured ? "text-white" : "text-ink"}`}>{c.title}</h2>
                <p className={`mt-3 text-sm leading-relaxed ${c.featured ? "text-white/65" : "text-ink/60"}`}>{c.body}</p>
                <span className={`mt-8 inline-flex items-center gap-2 text-sm font-semibold ${c.featured ? "bg-white px-5 py-3 text-ink" : "text-blueprint"}`}>
                  {c.cta}
                  <ArrowUpRight size={15} />
                </span>
              </>
            );
            const cls = `group relative flex h-full flex-col p-8 transition-colors duration-300 ${
              c.featured ? "bg-ink" : "bg-white hover:bg-paper"
            }`;
            return (
              <Reveal key={c.n} delay={i * 0.08} className="h-full">
                {c.external ? (
                  <a href={c.href} target="_blank" rel="noopener noreferrer" data-testid={c.testid} className={cls}>{inner}</a>
                ) : c.href.startsWith("mailto") ? (
                  <a href={c.href} data-testid={c.testid} className={cls}>{inner}</a>
                ) : (
                  <Link to={c.href} data-testid={c.testid} className={cls}>{inner}</Link>
                )}
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.15} className="mt-10">
          <div className="flex flex-col items-start justify-between gap-4 border border-border bg-paper p-6 md:flex-row md:items-center" data-testid="contact-response-note">
            <p className="text-sm leading-relaxed text-ink/70">
              <span className="font-semibold text-ink">Response time.</span> We reach out in less than one business day after a request is submitted.
            </p>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-ink/45">
              Mon–Fri · Central Time
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default Contact;
